import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiService } from "../../../services/apiService";
import interviewAuthService from "../../../services/interviewAuthService";
import logoImg from "../../../assets/images/screenup-ai-white.svg";
import removeImg from "../../../assets/images/remove-icon.svg";
import Header from "../../commonComponents/header/Header";

const Camera = () => {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState(
    "Please wait while we process the information.",
  );
  const [error, setError] = useState(null);
  const [interviewId, setInterviewId] = useState(null);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Initialize interview auth service and get interview ID
        interviewAuthService.initialize();

        // Add a small delay to ensure token is properly loaded
        await new Promise((resolve) => setTimeout(resolve, 100));

        // Check if user is authenticated
        // if (!interviewAuthService.isAuthenticated()) {
        //   console.error("User not authenticated for interview");
        //   navigate("/login");
        //   return;
        // }

        const token = interviewAuthService.getToken();
        const userInfo = interviewAuthService.getUserInfo();

        // Try multiple ways to get the interview ID
        const id1 = interviewAuthService.getInterviewId();
        const id2 = userInfo?.interview_id;

        // Use whichever method works
        const id = id1 || id2;

        if (!id) {
          console.error("No interview ID found in JWT token");
          console.error(
            "Token decoded successfully but missing interview_id field",
          );

          // Let's debug the token structure
          interviewAuthService.debugToken();

          // Try to get it from sessionStorage directly as a last resort
          const tokenFromStorage = sessionStorage.getItem("interviewAuthToken");
          if (tokenFromStorage) {
            try {
              const decodedFromStorage =
                interviewAuthService.decodeJWT(tokenFromStorage);
              if (decodedFromStorage?.interview_id) {
                setInterviewId(decodedFromStorage.interview_id);
                return;
              }
            } catch (e) {
              console.error("Failed to decode token from storage:", e);
            }
          }

          setError("Assessment session not found. Please login again.");
          return;
        }

        setInterviewId(id);
      } catch (error) {
        console.error("Error in initializeAuth:", error);
        setError("Failed to initialize authentication. Please try again.");
      }
    };

    initializeAuth();

    // Start camera when component mounts
    setTimeout(() => {
      startCamera();
    }, 200);

    // Add spacebar event listener
    const handleKeyPress = (event) => {
      if (event.code === "Space" || event.key === " ") {
        event.preventDefault();
        event.stopPropagation();
        captureImage();
      }
    };

    const handleKeyUp = (e) => {};

    // Add listeners to both document and window for better coverage
    document.addEventListener("keydown", handleKeyPress, true); // Use capture phase
    window.addEventListener("keydown", handleKeyPress);
    document.addEventListener("keyup", handleKeyUp);

    // Also make sure the component div can receive focus
    const mainDiv = document.querySelector(".h-screen");
    if (mainDiv) {
      mainDiv.tabIndex = 0;
      mainDiv.focus();
    }

    // Cleanup function
    return () => {
      document.removeEventListener("keydown", handleKeyPress, true);
      window.removeEventListener("keydown", handleKeyPress);
      document.removeEventListener("keyup", handleKeyUp);
    };
  }, [navigate]);
  // Listen for global cleanup events (like EndInterviewOverlay)
  useEffect(() => {
    const stopCamera = () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
        setStream(null);
      }
    };

    window.addEventListener("cleanup-all-media", stopCamera);

    return () => {
      window.removeEventListener("cleanup-all-media", stopCamera);
    };
  }, [stream]);
  // Separate cleanup effect for stream
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [stream]);

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: "user",
        },
        audio: false,
      });

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        setStream(mediaStream);

        // Wait for video to be ready
        videoRef.current.onloadedmetadata = () => {};
      }
    } catch (error) {
      console.error("Error accessing camera:", error);
      setError(
        "Unable to access camera. Please ensure camera permissions are granted.",
      );
    }
  };

  const captureImage = async () => {
    // Ensure we have a fresh interview ID
    const currentInterviewId = sessionStorage.getItem("interviewId");

    if (!videoRef.current) {
      return;
    }

    if (isCapturing) {
      return;
    }

    if (!currentInterviewId) {
      console.error("No interview ID available");
      console.error("interviewId state:", interviewId);
      console.error(
        "Auth service getInterviewId():",
        interviewAuthService.getInterviewId(),
      );
      console.error("User info:", interviewAuthService.getUserInfo());
      setError("Assessment session not found. Please login again.");
      return;
    }

    // Check if video is ready
    if (
      videoRef.current.videoWidth === 0 ||
      videoRef.current.videoHeight === 0
    ) {
      console.error("Video not ready - dimensions are 0");
      return;
    }

    setIsCapturing(true);
    setError(null);

    const canvas = canvasRef.current;
    const video = videoRef.current;
    const context = canvas.getContext("2d");

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw the video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Show success message immediately
    setShowSuccess(true);
    setProcessingMessage("Processing ID information...");

    // Convert canvas to blob and send to API
    canvas.toBlob(
      async (blob) => {
        if (blob) {
          try {
            const apiResponse = await apiService.extractIdData(
              currentInterviewId,
              blob,
            );

            // Check ONLY HTTP status code
            if (!apiResponse || apiResponse.status !== 200) {
              setError("Failed to process ID. Please try again.");
              setShowSuccess(false);
              setIsCapturing(false);
              return;
            }

            // SUCCESS → proceed
            setProcessingMessage(
              "ID processed successfully! Redirecting to assessment...",
            );

            // Stop camera after successful API call
            setTimeout(() => {
              if (stream) {
                stream.getTracks().forEach((track) => track.stop());
              }

              setTimeout(() => {
                navigate("/all-set");
              }, 1000);
            }, 2000);
          } catch (error) {
            console.error("Error calling ID extraction API:", error);
            setError("Failed to process ID. Please try again.");
            setShowSuccess(false);
            setIsCapturing(false);
          }
        } else {
          console.error("Failed to create blob from canvas");
          setError("Failed to capture image. Please try again.");
          setShowSuccess(false);
          setIsCapturing(false);
        }
      },
      "image/jpeg",
      0.8,
    );
  };

  // Display authentication info if available
  const userInfo = interviewAuthService.getUserInfo();

  return (
    <div
      className="h-screen w-screen bg-[#1F1F1F]  box-border flex flex-col relative overflow-hidden"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.code === "Space" || e.key === " ") {
          e.preventDefault();
          captureImage();
        }
      }}
      style={{ outline: "none" }}
    >
      <div className="absolute top-0 left-0 w-full z-50 p-4">
        <Header isWhite={true} />
      </div>

      {/* Error Message */}
      {error && (
        <div className="fixed flex top-20 left-1/2 transform -translate-x-1/2 z-50 bg-errorColor text-white px-6 py-3 rounded-lg shadow-lg ">
          <p className="text-secondaryFontSize font-normal">{error}</p>
          <button
            onClick={() => setError(null)}
            className="ml-4 text-white hover:text-gray-200"
          >
            <img
              src={removeImg}
              alt="remove"
              className="w-3 h-3 cursor-pointer"
            />
          </button>
        </div>
      )}

      {/* Main Content */}
      {/* <div className="flex-1 flex items-center justify-center relative"> */}
      {/* Video Container */}
      <div className="relative w-full h-full  bg-black  overflow-hidden">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="w-full h-full object-cover"
        />

        {/* ID Card Guide Rectangle */}
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[32rem] h-[24rem] border-4 border-red-500 border-dashed rounded-lg flex items-center justify-center bg-black bg-opacity-30">
          <div className="text-white text-center text-secondaryFontSize font-medium">
            {/* <div className="mb-2">📄</div> */}
            <div>Hold ID Card Here</div>
          </div>
        </div>

        {/* Instructions Overlay */}
        {!showSuccess && (
          <div className="absolute top-20 left-28 right-28 rounded-lg gap-1 bg-black bg-opacity-80 text-white p-6 flex flex-col items-center justify-start">
            <p className="app-body">
              Please display your Government approved ID.
            </p>
            <p className="app-body">
              Hold the ID within the viewfinder (Red Box) and press the SPACE
              key when you are ready.
            </p>
          </div>
        )}

        {/* Success Message */}
        {showSuccess && (
          <div className="absolute bottom-36 left-1/2 transform -translate-x-1/2 rounded-lg bg-black bg-opacity-80 text-white px-[80px] pb-12 pt-[20px] flex flex-col items-center justify-center mt-[20px]">
            <div className="text-center text-white">
              <h2 className="app-title font-medium text-white mb-[30px]">
                Thank you!
              </h2>

              <p className="app-body mb-[20px]">
                A picture of your ID was captured.
              </p>

              <div className="flex items-center justify-center mb-[20px]">
                <div className="relative w-14 h-14 animate-spin-slow">
                  {[...Array(12)].map((_, i) => (
                    <span
                      key={i}
                      className="absolute left-1/2 top-1/2 block w-1.5 h-3 bg-white rounded animate-spinner-bar"
                      style={{
                        transform: `rotate(${i * 30}deg) translate(0, -170%)`,
                        animationDelay: `${i * 0.1}s`,
                      }}
                    />
                  ))}
                </div>
              </div>

              <p>Processing...</p>
            </div>
          </div>
        )}
      </div>
      {/* </div> */}

      {/* Hidden canvas for image capture */}
      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
};

export default Camera;
